/**
 * Portal Shell (Story 14.3 — Task 9.1 / AC #8 / AC #9 / AC #13 / AC #14)
 *
 * The full-dashboard surface for a successfully opened portal link. Renders:
 *   - Top bar: tenant logo, client name + label, date-range selector, refresh,
 *              "last refreshed X ago" indicator, Powered-by-ProvenLeap toggle
 *   - Body: responsive widget grid (320px min column)
 *   - Footer: optional PoweredBy link + expiry note
 *
 * Auto-refetch: `refetchInterval: 5 * 60 * 1000` (AC #8). Manual refresh uses
 * `?refresh=1` to bypass the server-side 60s KV cache.
 *
 * A11y (AC #14):
 *   - <header>, <main aria-labelledby>, <footer> landmarks
 *   - <h1> inside header carries the client name + label
 *   - Skip-link to #main-content
 *   - aria-live="polite" on the "last refreshed" indicator
 *   - Keyboard-focusable controls (date range dropdown, refresh button)
 *   - Contrast-aware text color when tenant sets a custom primaryColor
 *
 * NO DashboardLayout wrapper. NO sidebar / user menu / team switcher.
 * The portal is a public, minimal surface.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@provenleap/ui";
import {
  getContrastingText,
  type PortalMetaActive,
  type PortalViewResponse,
  type PortalDateRangePreset,
  type PortalComparisonPreset,
} from "@provenleap/types";
import {
  fetchPortalView,
  portalDownloadUrl,
  PortalApiError,
  type FetchPortalViewOptions,
} from "../../lib/portalPublicApi";
import { WidgetRenderer } from "../reports/widgets/WidgetRenderer";
import type { WidgetData } from "../../lib/widgetData";
import type { WidgetConfig } from "../../lib/templateUtils";
import { PortalInactivePage } from "./PortalInactivePage";

/**
 * Backend seam (additive, default-preserving).
 *
 * PortalShell is reused by two public surfaces that hit DIFFERENT server routes
 * with byte-identical response shapes:
 *   - The report portal (`/portal/:token`)        → `/api/portal-view/*`
 *   - The dashboard share view (`/dashboard-view/:token`) → `/api/dashboard-share-view/*`
 *
 * The default backend below points at the report portal's `portalPublicApi`, so
 * an omitted `api` prop behaves EXACTLY as before. The dashboard route supplies
 * its own backend (dashboardShareApi) via the `api` prop so widgets actually load
 * from the dashboard endpoint.
 *
 * `isPasswordRequiredError` exists because each backend throws its OWN error
 * class (PortalApiError vs DashboardShareApiError) — an `instanceof` check
 * against a single class would silently fail for the other surface. The predicate
 * keeps the password-required detection backend-agnostic without coupling.
 */
export interface PortalShellBackend {
  fetchView(
    token: string,
    options: FetchPortalViewOptions,
  ): Promise<PortalViewResponse | import("@provenleap/types").PortalMetaInactive>;
  downloadUrl(token: string): string;
  /** True when the thrown error is the well-known "password_required" signal. */
  isPasswordRequiredError(error: unknown): boolean;
}

/** Default backend — the report portal (`/api/portal-view/*`). Byte-identical to pre-seam behavior. */
const DEFAULT_PORTAL_SHELL_BACKEND: PortalShellBackend = {
  fetchView: (token, options) => fetchPortalView(token, options),
  downloadUrl: (token) => portalDownloadUrl(token),
  isPasswordRequiredError: (error) =>
    error instanceof PortalApiError && error.message === "password_required",
};

const DATE_RANGE_OPTIONS: { value: PortalDateRangePreset; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "28d", label: "Last 28 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "mtd", label: "Month to date" },
  { value: "ytd", label: "Year to date" },
];

const AUTO_REFETCH_MS = 5 * 60 * 1000;
const SHOW_SKELETON_AFTER_MS = 200;

export interface PortalShellProps {
  token: string;
  meta: PortalMetaActive;
  /** Called when view reports password_required — parent re-renders password gate */
  onPasswordRequired: () => void;
  /**
   * Optional backend seam — defaults to the report portal (`/api/portal-view/*`).
   * The dashboard share route passes the dashboard backend so widgets load from
   * `/api/dashboard-share-view/*`. Omit for byte-identical report-portal behavior.
   */
  api?: PortalShellBackend;
}

export function PortalShell({
  token,
  meta,
  onPasswordRequired,
  api = DEFAULT_PORTAL_SHELL_BACKEND,
}: PortalShellProps) {
  const [dateRange, setDateRange] = useState<PortalDateRangePreset>("28d");
  // Comparison is fixed to `previous_period` in v1 — no UI dropdown for it yet.
  // Story 14.5 may expose a comparison picker; for now the default is the one
  // agencies expect from competing portal products (AgencyAnalytics, DashThis).
  const comparison: PortalComparisonPreset = "previous_period";
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [isInactiveNow, setIsInactiveNow] = useState(false);
  // Ref used so the manual-Refresh click can instruct the next queryFn run to
  // append `?refresh=1` (AC #20). Cleared after one run so auto-polling does
  // NOT hit the cache-bypass path — only explicit button clicks do.
  const manualRefreshRef = useRef(false);

  const query = useQuery({
    queryKey: ["portal-view", token, dateRange, comparison],
    queryFn: async () => {
      const refresh = manualRefreshRef.current;
      manualRefreshRef.current = false;
      const result = await api.fetchView(token, {
        dateRange,
        comparison,
        refresh,
      });
      return result;
    },
    refetchInterval: AUTO_REFETCH_MS,
    retry: false,
  });

  const handleManualRefresh = () => {
    manualRefreshRef.current = true;
    query.refetch();
  };

  // Story 7.11 — the "Download PDF" button shows ONLY for report-scoped links whose
  // report actually has a rendered PDF (`downloadable`), so it never dead-ends on a
  // report shared before it was rendered. Full-scope live dashboards have no single
  // PDF. The download is a token-derived, pre-auth stream; navigating to the
  // endpoint sends the portal session cookie.
  const canDownload = meta.scope === "report" && meta.downloadable;

  const handleDownloadPdf = () => {
    // Direct browser navigation so the Content-Disposition attachment is handled
    // natively (no blob juggling). Same-origin → the portal session cookie rides
    // along automatically for password-protected links.
    window.location.href = api.downloadUrl(token);
  };

  // Detect password_required surfaces mid-session (rare — cookie expired during polling)
  useEffect(() => {
    if (api.isPasswordRequiredError(query.error)) {
      onPasswordRequired();
    }
  }, [api, query.error, onPasswordRequired]);

  // Detect inactive view response (link was revoked / expired mid-session)
  useEffect(() => {
    if (query.data && "status" in query.data && query.data.status === "inactive") {
      setIsInactiveNow(true);
    }
  }, [query.data]);

  // Debounce skeleton — avoid flash for <200ms fetches (AC #23)
  useEffect(() => {
    if (!query.isLoading) {
      setShowSkeleton(false);
      return;
    }
    const t = window.setTimeout(() => setShowSkeleton(true), SHOW_SKELETON_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [query.isLoading]);

  // Document title (AC #14). companyName is server-guaranteed non-null (it
  // falls back to tenant.name in the portal-view assembly), so no ProvenLeap
  // literal here — clientName is a defensive fallback only (Story 15.7).
  useEffect(() => {
    const prev = document.title;
    const companyName = meta.branding.companyName ?? meta.clientName;
    document.title = `${meta.clientName} Dashboard — ${companyName}`;
    return () => {
      document.title = prev;
    };
  }, [meta.clientName, meta.branding.companyName]);

  // Story 15.7: the no-logo header wordmark must NOT hardcode "ProvenLeap" — a
  // paid tenant (poweredByProvenLeap=false) with no logo would otherwise leak it
  // on the most-visible client surface. Gate it exactly like the password gate;
  // companyName is server-guaranteed non-null (falls back to tenant.name).
  const headerWordmark = meta.branding.poweredByProvenLeap
    ? "ProvenLeap"
    : (meta.branding.companyName ?? meta.clientName);

  // Custom primary color support (AC #14)
  const primaryColor = meta.branding.primaryColor ?? null;
  // Story 15.3: secondaryColor drives the header/footer BORDER (structural),
  // while primaryColor stays the text/link accent. The portal is "branded" when
  // EITHER is set. The border CSS falls back secondary → primary → default, so a
  // tenant with only primaryColor keeps the exact prior border (no visual change).
  const secondaryColor = meta.branding.secondaryColor ?? null;
  const branded = Boolean(primaryColor || secondaryColor);
  const contrastingText = useMemo(() => getContrastingText(primaryColor), [primaryColor]);
  const brandStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!branded) return undefined;
    // primaryColor/secondaryColor are written into CSS custom properties on this
    // public viewer. They are trusted ONLY because the branding PATCH write path
    // hex-regex-validates both colors (update.ts). A malformed value would merely
    // no-op the CSS var (React escapes the style value — no attribute breakout).
    // If a future write path bypasses that regex, sanitize here.
    const s: Record<string, string> = {};
    if (primaryColor) s["--portal-accent"] = primaryColor;
    if (secondaryColor) s["--portal-accent-secondary"] = secondaryColor;
    return s as React.CSSProperties;
  }, [branded, primaryColor, secondaryColor]);

  const view: PortalViewResponse | null =
    query.data && "status" in query.data && query.data.status === "active"
      ? query.data
      : null;

  if (isInactiveNow) {
    return <PortalInactivePage />;
  }

  return (
    <>
      {/* Skip link — keyboard-only users tab to this first (AC #14) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-background focus:border-[0.5px] focus:border-[hsl(214_25%_70%)] focus:rounded-2xl focus:px-3 focus:py-1.5 focus:text-[13px]"
      >
        Skip to content
      </a>

      <div className="min-h-screen flex flex-col bg-background text-foreground">
        {/* Top bar — AC #16: when branded, the border accent consumes the
            runtime `--portal-accent` variable via the .portal-accent-border
            class scoped to `[data-portal-branded]`. Unbranded portals keep
            the default hsl(214 25% 70%) border. */}
        <header
          data-portal-branded={branded ? "" : undefined}
          className={
            branded
              ? "border-b-[0.5px] portal-accent-secondary-border bg-card"
              : "border-b-[0.5px] border-[hsl(214_25%_70%)] bg-card"
          }
          style={brandStyle}
        >
          <div className="mx-auto max-w-[1800px] px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {meta.branding.logoUrl ? (
                // Security: SVG branding logos MUST render via <img src>, never
                // <object>/<iframe>/<embed>/inlined. See Story 14.5 AC #4 +
                // CVE-2026-33172 pattern.
                <img
                  src={meta.branding.logoUrl}
                  alt=""
                  className="h-8 w-auto shrink-0"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="text-sm font-semibold tracking-tight text-muted-foreground">
                  {headerWordmark}
                </span>
              )}
              <div className="min-w-0">
                <h1 id="portal-heading" className="text-[15px] font-semibold tracking-tight truncate">
                  {meta.clientName}
                </h1>
                <p className="text-[13px] text-muted-foreground truncate">{meta.label}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={dateRange}
                onValueChange={(v) => setDateRange(v as PortalDateRangePreset)}
              >
                <SelectTrigger className="h-9 text-[13px] min-w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-[13px]">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="sm"
                onClick={handleManualRefresh}
                disabled={query.isFetching}
                className="text-[13px] transition-transform hover:-translate-y-0.5 hover:shadow-sm"
                aria-label="Refresh dashboard"
                title="Refresh"
              >
                <ArrowClockwise
                  size={16}
                  weight="bold"
                  className={query.isFetching ? "animate-spin" : undefined}
                />
              </Button>

              {/* Story 7.11 — Download PDF (report-scoped links only). */}
              {canDownload ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPdf}
                  className="text-[13px] transition-transform hover:-translate-y-0.5 hover:shadow-sm"
                  aria-label="Download PDF"
                  title="Download PDF"
                  data-testid="portal-download-pdf"
                >
                  <DownloadSimple size={16} weight="bold" className="mr-1.5" />
                  Download PDF
                </Button>
              ) : null}

              <div
                className="text-[11px] text-muted-foreground min-w-[120px] text-right"
                aria-live="polite"
              >
                {view ? <LastRefreshedIndicator iso={view.lastRefreshed} /> : null}
              </div>
            </div>
          </div>
        </header>

        {/* Body */}
        <main
          id="main-content"
          aria-labelledby="portal-heading"
          className="flex-1 mx-auto w-full max-w-[1800px] px-4 sm:px-6 py-6"
        >
          {showSkeleton && !view ? <PortalSkeleton /> : null}
          {query.isError && !api.isPasswordRequiredError(query.error) ? (
            <div
              role="alert"
              className="rounded-3xl border-[0.5px] border-[hsl(214_25%_70%)] bg-card p-6 text-[13px] text-muted-foreground"
            >
              We couldn&apos;t load the dashboard right now. Please try again in a moment.
            </div>
          ) : null}
          {view ? (
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              }}
            >
              {view.widgets.map((envelope) => {
                const cfg: WidgetConfig = {
                  id: envelope.id,
                  type: envelope.type as WidgetConfig["type"],
                  title: envelope.title,
                  position: envelope.position,
                  config: envelope.config,
                } as WidgetConfig;
                const widgetData: Record<string, WidgetData> = {
                  [envelope.id]: envelope.data as WidgetData,
                };
                return (
                  <div key={envelope.id} className="min-h-0">
                    <WidgetRenderer widget={cfg} widgetData={widgetData} />
                  </div>
                );
              })}
            </div>
          ) : null}
        </main>

        {/* Footer */}
        <footer
          data-portal-branded={branded ? "" : undefined}
          className={
            branded
              ? "border-t-[0.5px] portal-accent-secondary-border bg-card"
              : "border-t-[0.5px] border-[hsl(214_25%_70%)] bg-card"
          }
          style={brandStyle}
        >
          <div className="mx-auto max-w-[1800px] px-4 sm:px-6 py-3 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
            {/* Left group: optional agency Help link (Story 15.7) + the
                plan-gated "Powered by ProvenLeap". An always-present (possibly
                empty) container keeps justify-between pushing the expiry right. */}
            <div className="flex items-center gap-4">
              {/* Render-time https guard (defense-in-depth): the write path
                  Zod-validates https, but a future non-validated writer (demo
                  seed, migration, gated 15.4 endpoint) must never surface a
                  javascript:/data: href on this public viewer. */}
              {meta.branding.helpUrl?.startsWith("https://") ? (
                <a
                  href={meta.branding.helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors"
                >
                  Help
                </a>
              ) : null}
              {meta.branding.poweredByProvenLeap ? (
                // AC #16: when branded, use the class-based .portal-accent-text
                // rule so the link color inherits --portal-accent. Keep the
                // inline style as a contrast-ensuring override when the accent
                // color fails WCAG against the footer background.
                <a
                  href="https://provenleap.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    primaryColor
                      ? "portal-accent-text hover:opacity-80 transition-opacity"
                      : "hover:text-foreground transition-colors"
                  }
                  style={primaryColor ? { color: contrastingText } : undefined}
                >
                  Powered by ProvenLeap
                </a>
              ) : null}
            </div>
            <ExpiryNote meta={meta} />
          </div>
        </footer>
      </div>
    </>
  );
}

// ============================================================================
// Internal helpers
// ============================================================================

function LastRefreshedIndicator({ iso }: { iso: string }) {
  const [, force] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => force((v) => v + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffSec = Math.round(diffMs / 1000);

  let text: string;
  if (diffSec < 60) {
    text = "just now";
  } else if (diffSec < 3600) {
    text = rtf.format(-Math.round(diffSec / 60), "minute");
  } else if (diffSec < 86_400) {
    text = rtf.format(-Math.round(diffSec / 3600), "hour");
  } else {
    text = rtf.format(-Math.round(diffSec / 86_400), "day");
  }

  return <span>Updated {text}</span>;
}

function ExpiryNote({ meta }: { meta: PortalMetaActive }) {
  if (meta.expiresAt == null) return null;
  const expiresDate = new Date(meta.expiresAt);
  const formatted = expiresDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return <span>Share link expires on {formatted}</span>;
}

function PortalSkeleton() {
  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
      aria-label="Loading dashboard"
    >
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="h-32 rounded-3xl border-[0.5px] border-[hsl(214_25%_70%)] bg-muted animate-pulse"
        />
      ))}
      {[1, 2].map((i) => (
        <div
          key={`chart-${i}`}
          className="h-64 rounded-3xl border-[0.5px] border-[hsl(214_25%_70%)] bg-muted animate-pulse col-span-full"
        />
      ))}
    </div>
  );
}
